import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowLeft,
  Copy,
  Focus,
  Globe,
  GripVertical,
  ImagePlus,
  Lock,
  LockOpen,
  Search,
  Settings,
  Trash2,
  Undo2,
  Upload,
  Users,
  UserX,
  X,
} from 'lucide-react';
import {
  ApiError,
  api,
  canEdit,
  type AuthUser,
  type PhotoAlbum,
  type PhotoAlbumEntry,
  type PhotoAlbumEntryPlayer,
} from '@/lib/api';

const RANK_ORDER: Record<string, number> = { R5: 5, R4: 4, R3: 3, R2: 2, R1: 1 };

export default function PhotoAlbumDetail({ user }: { user: AuthUser }) {
  const { id } = useParams();
  const albumId = Number(id);
  const editable = canEdit(user.role);
  const [showSettings, setShowSettings] = useState(false);
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const detailQuery = useQuery({
    queryKey: ['photo-album', albumId],
    queryFn: () => api.getPhotoAlbum(albumId),
    enabled: !!albumId,
  });

  if (detailQuery.isLoading) return <div className="text-slate-400">Loading…</div>;
  if (detailQuery.isError || !detailQuery.data)
    return <div className="text-red-400">Album not found.</div>;

  const { album, entries, emptyPlayers, declinedPlayers } = detailQuery.data;
  const readOnly = !editable || album.status === 'closed';

  const visibleEntries = onlyMissing ? entries.filter((e) => !e.hasPhoto) : entries;
  const visibleEmpty = emptyPlayers;
  const visibleDeclined = declinedPlayers ?? [];

  return (
    <div className="space-y-4">
      <Link
        to="/albums"
        className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200"
      >
        <ArrowLeft size={14} /> Back to albums
      </Link>

      <AlbumHeader
        album={album}
        editable={editable}
        onToggleSettings={() => setShowSettings((v) => !v)}
        settingsOpen={showSettings}
      />

      {showSettings && editable && <AlbumSettings album={album} />}
      {showSettings && editable && <AlbumAccessLog albumId={albumId} />}

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label className="inline-flex items-center gap-2 text-slate-300">
          <input
            type="checkbox"
            checked={onlyMissing}
            onChange={(e) => setOnlyMissing(e.target.checked)}
          />
          Only players without photo
        </label>
        <span className="text-xs text-slate-500">
          {visibleEntries.length} entries · {visibleEmpty.length} unassigned players
        </span>
        <div className="ml-auto flex items-center gap-2">
          {editable && !readOnly && entries.length > 0 && (
            <button
              type="button"
              onClick={() => setBulkOpen(true)}
              className="inline-flex items-center gap-1.5 rounded border border-slate-700 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-800"
              title="Reassign multiple entries at once — e.g. after a chain of account transfers"
            >
              <Users size={14} /> Bulk reassign
            </button>
          )}
          {editable && <AddFreeEntryButton albumId={albumId} readOnly={readOnly} />}
        </div>
      </div>

      {editable && !readOnly && (
        <BulkReassignDialog
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          albumId={albumId}
          entries={entries}
        />
      )}

      <EntriesGrid
        albumId={albumId}
        entries={visibleEntries}
        readOnly={readOnly}
      />

      {visibleEmpty.length > 0 && (
        <section className="space-y-2 pt-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
            Players without an entry ({visibleEmpty.length})
          </h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[...visibleEmpty]
              .sort((a, b) => {
                const r = (RANK_ORDER[b.rank] ?? 0) - (RANK_ORDER[a.rank] ?? 0);
                if (r) return r;
                return a.name.localeCompare(b.name);
              })
              .map((p) => (
                <EmptyPlayerCard
                  key={p.id}
                  albumId={albumId}
                  player={p}
                  readOnly={readOnly}
                />
              ))}
          </div>
        </section>
      )}

      {visibleDeclined.length > 0 && (
        <DeclinedSection
          albumId={albumId}
          players={visibleDeclined}
          readOnly={readOnly}
        />
      )}

      <Style />
    </div>
  );
}

// ------------ header + settings (kept as before) ------------

function AlbumHeader({
  album,
  editable,
  onToggleSettings,
  settingsOpen,
}: {
  album: PhotoAlbum;
  editable: boolean;
  onToggleSettings: () => void;
  settingsOpen: boolean;
}) {
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: () =>
      api.updatePhotoAlbum(album.id, {
        status: album.status === 'open' ? 'closed' : 'open',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['photo-album', album.id] });
      qc.invalidateQueries({ queryKey: ['photo-albums'] });
    },
  });
  const { playersWithPhoto, totalPlayers, freePhotos } = album.counts;
  const pct = totalPlayers > 0 ? (playersWithPhoto / totalPlayers) * 100 : 0;

  const publicUrl =
    album.published && album.publicSlug
      ? `${window.location.origin}/a/${album.publicSlug}`
      : null;

  return (
    <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1">
          <h1 className="text-xl font-semibold">{album.title}</h1>
          {album.description && (
            <p className="mt-2 whitespace-pre-wrap text-sm text-slate-300">
              {album.description}
            </p>
          )}
          <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-500">
            <span>Created {album.createdAt.slice(0, 10)}</span>
            <span
              className={
                album.status === 'open'
                  ? 'rounded bg-emerald-700/50 px-2 py-0.5 text-emerald-100'
                  : 'rounded bg-slate-700 px-2 py-0.5 text-slate-300'
              }
            >
              {album.status}
            </span>
            {album.published ? (
              <span className="inline-flex items-center gap-1 rounded bg-indigo-700/60 px-2 py-0.5 text-indigo-100">
                <Globe size={11} /> published
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded bg-slate-700 px-2 py-0.5 text-slate-300">
                unpublished
              </span>
            )}
            {album.closedAt && <span>Closed {album.closedAt.slice(0, 10)}</span>}
          </div>
          {publicUrl && (
            <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
              <a
                href={publicUrl}
                target="_blank"
                rel="noreferrer"
                className="text-indigo-300 hover:underline"
              >
                {publicUrl}
              </a>
              <button
                type="button"
                onClick={() => navigator.clipboard.writeText(publicUrl)}
                className="rounded border border-slate-700 p-1 hover:bg-slate-800"
                title="Copy public URL"
              >
                <Copy size={12} />
              </button>
            </div>
          )}
          {album.hasPassword && (
            <div
              className="mt-2 text-xs text-slate-400"
              title={
                album.access.lastAt
                  ? `Last on ${new Date(album.access.lastAt + 'Z').toLocaleString()}`
                  : 'No password uses yet'
              }
            >
              Password used{' '}
              <span className="tabular-nums text-slate-200">{album.access.count}</span>{' '}
              {album.access.count === 1 ? 'time' : 'times'}
              {album.access.lastAt && (
                <>
                  {' · last '}
                  {new Date(album.access.lastAt + 'Z').toLocaleString()}
                </>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          {editable && (
            <button
              onClick={onToggleSettings}
              className={`flex items-center gap-1 rounded border px-3 py-1.5 text-sm ${
                settingsOpen
                  ? 'border-indigo-500 bg-indigo-900/40 text-indigo-100'
                  : 'border-slate-700 hover:bg-slate-800'
              }`}
            >
              <Settings size={14} /> Sharing
            </button>
          )}
          {editable && (
            <button
              onClick={() => toggle.mutate()}
              disabled={toggle.isPending}
              className="flex items-center gap-1 rounded border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800 disabled:opacity-50"
            >
              {album.status === 'open' ? (
                <>
                  <Lock size={14} /> Close album
                </>
              ) : (
                <>
                  <LockOpen size={14} /> Reopen
                </>
              )}
            </button>
          )}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-400">
        <span className="tabular-nums" title="Members with a photo in this album">
          {playersWithPhoto} / {totalPlayers} members
        </span>
        <span className="tabular-nums text-slate-500" title="Free (non-member) photos">
          +{freePhotos} free
        </span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded bg-slate-800">
        <div className="h-full bg-indigo-500" style={{ width: `${pct}%` }} />
      </div>
    </section>
  );
}

function AlbumSettings({ album }: { album: PhotoAlbum }) {
  const qc = useQueryClient();
  const [slug, setSlug] = useState(album.publicSlug ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    setSlug(album.publicSlug ?? '');
  }, [album.publicSlug]);

  const savePublish = useMutation({
    mutationFn: (published: boolean) =>
      api.updatePhotoAlbum(album.id, { published }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['photo-album', album.id] });
      qc.invalidateQueries({ queryKey: ['photo-albums'] });
      setError(null);
      setSavedAt(Date.now());
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Save failed'),
  });

  const saveSlug = useMutation({
    mutationFn: (val: string | null) =>
      api.updatePhotoAlbum(album.id, { publicSlug: val }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['photo-album', album.id] });
      setError(null);
      setSavedAt(Date.now());
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Slug save failed'),
  });

  const savePassword = useMutation({
    mutationFn: (val: string | null) =>
      api.updatePhotoAlbum(album.id, { password: val }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['photo-album', album.id] });
      setPassword('');
      setError(null);
      setSavedAt(Date.now());
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Password save failed'),
  });

  return (
    <section className="space-y-3 rounded-lg border border-slate-800 bg-slate-900/40 p-4 text-sm">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
        Public sharing
      </h2>
      <div>
        <label className="mb-1 block text-xs uppercase tracking-wider text-slate-400">
          Public slug (lowercase, hyphens)
        </label>
        <div className="flex gap-2">
          <input
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="foa-2026-summer"
            className="input"
          />
          <button
            type="button"
            onClick={() => saveSlug.mutate(slug.trim() ? slug.trim() : null)}
            disabled={saveSlug.isPending}
            className="rounded bg-indigo-600 px-3 py-1.5 text-xs hover:bg-indigo-500 disabled:opacity-50"
          >
            Save slug
          </button>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Short URL: <code>/a/&lt;slug&gt;</code>. Long form{' '}
          <code>/public/albums/&lt;slug&gt;</code> keeps working. Pick something
          short and memorable (e.g. <code>yb26</code>).
        </p>
      </div>
      <div>
        <label className="mb-1 block text-xs uppercase tracking-wider text-slate-400">
          Password {album.hasPassword && <span className="text-emerald-300">(set)</span>}
        </label>
        <div className="flex gap-2">
          <input
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type="password"
            placeholder={album.hasPassword ? '••••••• (replace)' : 'Choose a password'}
            className="input"
          />
          <button
            type="button"
            onClick={() => savePassword.mutate(password)}
            disabled={savePassword.isPending || password.length < 4}
            className="rounded bg-indigo-600 px-3 py-1.5 text-xs hover:bg-indigo-500 disabled:opacity-50"
          >
            Set
          </button>
          {album.hasPassword && (
            <button
              type="button"
              onClick={() => {
                if (confirm('Remove password? Album will need to be unpublished.'))
                  savePassword.mutate(null);
              }}
              className="rounded border border-slate-700 px-3 py-1.5 text-xs hover:bg-slate-800"
            >
              Clear
            </button>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 pt-2">
        <button
          type="button"
          onClick={() => savePublish.mutate(!album.published)}
          disabled={savePublish.isPending || (!album.published && !album.hasPassword)}
          className={`rounded px-4 py-1.5 text-sm ${
            album.published
              ? 'bg-red-700 hover:bg-red-600'
              : 'bg-emerald-700 hover:bg-emerald-600'
          } disabled:opacity-50`}
          title={
            !album.published && !album.hasPassword ? 'Set a password first' : undefined
          }
        >
          {album.published ? 'Unpublish' : 'Publish'}
        </button>
        {savedAt && !error && <span className="text-xs text-slate-500">Saved.</span>}
        {error && <span className="text-xs text-red-400">{error}</span>}
      </div>
    </section>
  );
}

function AlbumAccessLog({ albumId }: { albumId: number }) {
  const q = useQuery({
    queryKey: ['photo-album-access', albumId],
    queryFn: () => api.getPhotoAlbumAccess(albumId),
  });
  const rows = q.data?.rows ?? [];
  return (
    <section className="space-y-2 rounded-lg border border-slate-800 bg-slate-900/40 p-4 text-sm">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
        Password access log
      </h2>
      {q.isLoading && <div className="text-xs text-slate-500">Loading…</div>}
      {!q.isLoading && rows.length === 0 && (
        <div className="text-xs text-slate-500">
          No successful password entries yet.
        </div>
      )}
      {rows.length > 0 && (
        <div className="max-h-72 overflow-y-auto rounded border border-slate-800">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-900 text-left text-slate-400">
              <tr>
                <th className="px-2 py-1 font-normal">When</th>
                <th className="px-2 py-1 font-normal">IP</th>
                <th className="px-2 py-1 font-normal">User agent</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-800">
                  <td className="whitespace-nowrap px-2 py-1 tabular-nums text-slate-200">
                    {new Date(r.at + 'Z').toLocaleString()}
                  </td>
                  <td className="whitespace-nowrap px-2 py-1 text-slate-400">
                    {r.ip ?? '—'}
                  </td>
                  <td
                    className="max-w-[24rem] truncate px-2 py-1 text-slate-500"
                    title={r.userAgent ?? ''}
                  >
                    {r.userAgent ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ---------------- entries grid (drag & drop) ----------------

function EntriesGrid({
  albumId,
  entries,
  readOnly,
}: {
  albumId: number;
  entries: PhotoAlbumEntry[];
  readOnly: boolean;
}) {
  const qc = useQueryClient();
  const [localOrder, setLocalOrder] = useState<PhotoAlbumEntry[]>(entries);

  useEffect(() => {
    setLocalOrder(entries);
  }, [entries]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const reorder = useMutation({
    mutationFn: (ids: number[]) => api.reorderPhotoAlbumEntries(albumId, ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['photo-album', albumId] }),
  });

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = localOrder.findIndex((x) => x.entryId === active.id);
    const newIndex = localOrder.findIndex((x) => x.entryId === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(localOrder, oldIndex, newIndex);
    setLocalOrder(next);
    reorder.mutate(next.map((e) => e.entryId));
  };

  if (localOrder.length === 0) {
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-6 text-sm text-slate-400">
        No entries in this album yet.
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext
        items={localOrder.map((e) => e.entryId)}
        strategy={rectSortingStrategy}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {localOrder.map((entry) => (
            <SortableEntryCard
              key={entry.entryId}
              albumId={albumId}
              entry={entry}
              readOnly={readOnly}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableEntryCard({
  albumId,
  entry,
  readOnly,
}: {
  albumId: number;
  entry: PhotoAlbumEntry;
  readOnly: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entry.entryId, disabled: readOnly });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <EntryCard
        albumId={albumId}
        entry={entry}
        readOnly={readOnly}
        dragHandle={
          !readOnly && (
            <button
              type="button"
              className="cursor-grab rounded p-1 text-slate-500 hover:bg-slate-800 hover:text-slate-300 active:cursor-grabbing"
              title="Drag to reorder"
              {...attributes}
              {...listeners}
            >
              <GripVertical size={14} />
            </button>
          )
        }
      />
    </div>
  );
}

function EntryCard({
  albumId,
  entry,
  readOnly,
  dragHandle,
}: {
  albumId: number;
  entry: PhotoAlbumEntry;
  readOnly: boolean;
  dragHandle?: React.ReactNode;
}) {
  const qc = useQueryClient();
  const [bio, setBio] = useState(entry.bio ?? '');
  const [title, setTitle] = useState(entry.title ?? '');
  const [error, setError] = useState<string | null>(null);
  const [focusMode, setFocusMode] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setBio(entry.bio ?? '');
    setTitle(entry.title ?? '');
  }, [entry.entryId, entry.bio, entry.title]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['photo-album', albumId] });
    qc.invalidateQueries({ queryKey: ['photo-albums'] });
  };

  const patchEntry = useMutation({
    mutationFn: (patch: {
      bio?: string | null;
      title?: string | null;
      focusX?: number;
      focusY?: number;
    }) => api.patchPhotoAlbumEntryById(albumId, entry.entryId, patch),
    onSuccess: () => {
      invalidate();
      setError(null);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Save failed'),
  });

  const upload = useMutation({
    mutationFn: (file: File) =>
      api.uploadPhotoAlbumEntryPhotoById(albumId, entry.entryId, file),
    onSuccess: () => {
      invalidate();
      setError(null);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Upload failed'),
  });

  const removePhoto = useMutation({
    mutationFn: () => api.deletePhotoAlbumEntryPhotoById(albumId, entry.entryId),
    onSuccess: () => {
      invalidate();
      setError(null);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Delete failed'),
  });

  const deleteEntry = useMutation({
    mutationFn: () => api.deletePhotoAlbumFreeEntry(albumId, entry.entryId),
    onSuccess: () => {
      invalidate();
      setError(null);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Delete failed'),
  });

  const [reassignOpen, setReassignOpen] = useState(false);
  const reassign = useMutation({
    mutationFn: (input: { playerId: number; allowSwap?: boolean }) =>
      api.reassignPhotoAlbumEntry(albumId, entry.entryId, input.playerId, input.allowSwap ?? false),
    onSuccess: () => {
      invalidate();
      setError(null);
      setReassignOpen(false);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Reassign failed'),
  });

  const isFree = entry.player == null;
  const heading = entry.player
    ? entry.player.displayName?.trim() || entry.player.name
    : entry.title || 'Untitled';
  const subtitle = entry.player
    ? [entry.player.rank, entry.player.realName].filter(Boolean).join(' · ')
    : 'Free image';

  const photoUrl = entry.hasPhoto
    ? api.photoAlbumEntryPhotoUrlById(albumId, entry.entryId, entry.updatedAt ?? undefined)
    : null;

  const handleFocusClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!focusMode || readOnly || !entry.hasPhoto) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    patchEntry.mutate({ focusX: x, focusY: y });
  };

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-slate-800 bg-slate-900/40 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          {isFree && !readOnly ? (
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => {
                const t = title.trim();
                if (t !== (entry.title ?? '')) patchEntry.mutate({ title: t || null });
              }}
              placeholder="Untitled…"
              className="input font-medium"
            />
          ) : (
            <div className="truncate font-medium">{heading}</div>
          )}
          <div className="mt-0.5 truncate text-xs text-slate-500">{subtitle}</div>
        </div>
        <div className="flex items-center gap-1">
          {entry.hasPhoto && !readOnly && (
            <button
              type="button"
              onClick={() => setFocusMode((v) => !v)}
              className={`flex items-center gap-1 rounded border px-2 py-1 text-xs ${
                focusMode
                  ? 'border-amber-500 bg-amber-900/40 text-amber-100'
                  : 'border-slate-700 hover:bg-slate-800'
              }`}
              title={focusMode ? 'Click on image to set focus' : 'Adjust focus point'}
            >
              <Focus size={12} />
            </button>
          )}
          {dragHandle}
        </div>
      </div>

      <div
        onClick={handleFocusClick}
        className={`relative aspect-square w-full overflow-hidden rounded bg-slate-950 ${
          focusMode && entry.hasPhoto ? 'cursor-crosshair ring-2 ring-amber-500' : ''
        }`}
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={heading}
            className="h-full w-full object-cover"
            style={{
              objectPosition: `${entry.focusX * 100}% ${entry.focusY * 100}%`,
            }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-slate-600">
            No photo
          </div>
        )}
        {focusMode && entry.hasPhoto && (
          <div
            className="pointer-events-none absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-amber-400 bg-amber-500/40"
            style={{
              left: `${entry.focusX * 100}%`,
              top: `${entry.focusY * 100}%`,
            }}
          />
        )}
        {!readOnly && !focusMode && (
          <div className="absolute inset-x-0 bottom-0 flex gap-1 bg-gradient-to-t from-black/70 to-transparent p-2">
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={upload.isPending}
              className="flex items-center gap-1 rounded bg-indigo-600/90 px-2 py-1 text-xs hover:bg-indigo-500 disabled:opacity-50"
            >
              <Upload size={12} /> {upload.isPending ? 'Uploading…' : 'Upload'}
            </button>
            {entry.hasPhoto && (
              <button
                type="button"
                onClick={() => {
                  if (confirm('Remove photo?')) removePhoto.mutate();
                }}
                disabled={removePhoto.isPending}
                className="flex items-center gap-1 rounded border border-slate-700 bg-slate-900/80 px-2 py-1 text-xs hover:bg-slate-800 disabled:opacity-50"
              >
                <Trash2 size={12} />
              </button>
            )}
            <button
              type="button"
              onClick={() => setReassignOpen(true)}
              className="flex items-center gap-1 rounded border border-slate-700 bg-slate-900/80 px-2 py-1 text-xs hover:bg-slate-800"
              title={
                isFree
                  ? 'Assign this entry to a player'
                  : 'Assign this entry to a different player (e.g. after an account swap)'
              }
            >
              <Users size={12} /> {isFree ? 'Assign player' : 'Reassign'}
            </button>
            {isFree && (
              <button
                type="button"
                onClick={() => {
                  if (confirm('Delete this free entry entirely?')) deleteEntry.mutate();
                }}
                disabled={deleteEntry.isPending}
                className="ml-auto flex items-center gap-1 rounded border border-red-800 bg-red-900/50 px-2 py-1 text-xs text-red-100 hover:bg-red-800 disabled:opacity-50"
              >
                <X size={12} /> Remove entry
              </button>
            )}
          </div>
        )}
      </div>

      <input
        ref={fileInput}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) upload.mutate(f);
        }}
      />

      <textarea
        value={bio}
        disabled={readOnly}
        onChange={(e) => setBio(e.target.value)}
        onBlur={() => {
          const trimmed = bio.trim();
          if (trimmed !== (entry.bio ?? '')) {
            patchEntry.mutate({ bio: trimmed ? trimmed : null });
          }
        }}
        placeholder={readOnly ? '' : 'Short bio / caption…'}
        rows={3}
        className="input resize-none"
      />
      {error && <div className="text-xs text-red-400">{error}</div>}

      {reassignOpen && (
        <ReassignPlayerModal
          currentPlayer={entry.player}
          onClose={() => setReassignOpen(false)}
          onPick={(playerId) => reassign.mutate({ playerId })}
          onSwap={(playerId) => reassign.mutate({ playerId, allowSwap: true })}
          pending={reassign.isPending}
          error={reassign.error as ApiError | null}
        />
      )}
    </div>
  );
}

// ------------ reassign player modal ------------

function ReassignPlayerModal({
  currentPlayer,
  onClose,
  onPick,
  onSwap,
  pending,
  error,
}: {
  currentPlayer: PhotoAlbumEntryPlayer | null;
  onClose: () => void;
  onPick: (playerId: number) => void;
  onSwap: (playerId: number) => void;
  pending: boolean;
  error: ApiError | null;
}) {
  const [q, setQ] = useState('');
  const [lastPickedId, setLastPickedId] = useState<number | null>(null);

  const playersQuery = useQuery({
    queryKey: ['players', { includeInactive: true }],
    queryFn: () => api.listPlayers({ includeInactive: true }),
  });

  const players = playersQuery.data ?? [];
  const norm = q.trim().toLowerCase();
  const filtered = norm
    ? players.filter((p) => {
        const hay = [p.name, p.displayName, p.realName]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(norm);
      })
    : players;
  const shown = filtered.slice(0, 200);

  const conflict = error as (ApiError & { conflictEntryId?: number }) | null;
  const collisionForCurrent =
    conflict?.status === 409 &&
    conflict?.conflictEntryId != null &&
    lastPickedId != null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-md rounded-lg border border-slate-800 bg-slate-900 p-4 shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-white">
              {currentPlayer ? 'Reassign entry' : 'Assign entry to player'}
            </h3>
            {currentPlayer && (
              <div className="text-xs text-slate-500">
                Currently:&nbsp;
                <span className="text-slate-300">
                  {currentPlayer.displayName ?? currentPlayer.name}
                </span>
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X size={16} />
          </button>
        </div>

        <div className="mt-3 flex items-center gap-2 rounded border border-slate-700 bg-slate-950 px-2 py-1.5">
          <Search size={14} className="text-slate-500" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search player…"
            className="w-full bg-transparent text-sm text-slate-100 outline-none"
          />
        </div>

        <div className="mt-3 max-h-72 overflow-y-auto rounded border border-slate-800">
          {playersQuery.isLoading && (
            <div className="p-3 text-xs text-slate-500">Loading…</div>
          )}
          {!playersQuery.isLoading && shown.length === 0 && (
            <div className="p-3 text-xs text-slate-500">No matches.</div>
          )}
          <ul className="divide-y divide-slate-800/70">
            {shown.map((p) => {
              const isCurrent = currentPlayer?.id === p.id;
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    disabled={pending || isCurrent}
                    onClick={() => {
                      setLastPickedId(p.id);
                      onPick(p.id);
                    }}
                    className={
                      'flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm ' +
                      (isCurrent
                        ? 'cursor-default text-slate-500'
                        : 'hover:bg-slate-800')
                    }
                  >
                    <div className="min-w-0">
                      <div className="truncate text-slate-200">
                        {p.displayName ?? p.name}
                        {!p.active && (
                          <span className="ml-2 rounded bg-slate-800 px-1.5 py-0.5 text-[10px] uppercase text-slate-400">
                            inactive
                          </span>
                        )}
                      </div>
                      <div className="truncate text-xs text-slate-500">
                        {[p.rank, p.realName].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    {isCurrent && (
                      <span className="text-[10px] uppercase text-slate-500">current</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {collisionForCurrent && (
          <div className="mt-3 space-y-2 rounded border border-amber-700/50 bg-amber-950/30 p-3 text-xs text-amber-100">
            <div>{conflict?.message ?? 'That player already has an entry in this album.'}</div>
            <div>
              Swap the two entries? Both photos stay where they are — only the
              player pointers switch.
            </div>
            <button
              type="button"
              disabled={pending}
              onClick={() => onSwap(lastPickedId!)}
              className="rounded bg-amber-600 px-3 py-1 text-xs font-medium text-white hover:bg-amber-500 disabled:opacity-50"
            >
              Swap
            </button>
          </div>
        )}

        {error && !collisionForCurrent && (
          <div className="mt-3 rounded border border-red-800 bg-red-950/40 p-2 text-xs text-red-200">
            {error.message}
          </div>
        )}
      </div>
    </div>
  );
}

// ------------ bulk reassign dialog ------------

function BulkReassignDialog({
  open,
  onClose,
  albumId,
  entries,
}: {
  open: boolean;
  onClose: () => void;
  albumId: number;
  entries: PhotoAlbumEntry[];
}) {
  const qc = useQueryClient();
  // Move map: entryId → new playerId (null = free entry / leaver).
  const initial = useMemo(() => {
    const m = new Map<number, number | null>();
    for (const e of entries) m.set(e.entryId, e.player?.id ?? null);
    return m;
  }, [entries]);
  const [moves, setMoves] = useState<Map<number, number | null>>(initial);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setMoves(new Map(initial));
      setError(null);
    }
  }, [open, initial]);

  const playersQuery = useQuery({
    queryKey: ['players', { includeInactive: true }],
    queryFn: () => api.listPlayers({ includeInactive: true }),
    enabled: open,
  });
  const players = playersQuery.data ?? [];

  // Detect duplicate non-null targets so the user sees it before Apply.
  const dupTargets = useMemo(() => {
    const counts = new Map<number, number>();
    for (const pid of moves.values()) {
      if (pid == null) continue;
      counts.set(pid, (counts.get(pid) ?? 0) + 1);
    }
    return new Set([...counts].filter(([, n]) => n > 1).map(([pid]) => pid));
  }, [moves]);

  // Send the full desired end-state, not just changed rows — otherwise an
  // unchanged entry counts as "external" on the server and blocks moves
  // that target its player.
  const allMoves = useMemo(
    () =>
      entries.map((e) => ({
        entryId: e.entryId,
        toPlayerId: moves.get(e.entryId) ?? null,
      })),
    [entries, moves],
  );

  // Cosmetic count for the button — how many rows the user actually changed.
  const changedCount = useMemo(() => {
    let n = 0;
    for (const e of entries) {
      const wanted = moves.get(e.entryId) ?? null;
      const current = e.player?.id ?? null;
      if (wanted !== current) n++;
    }
    return n;
  }, [entries, moves]);

  const applyMutation = useMutation({
    mutationFn: () => api.reassignPhotoAlbumEntriesBatch(albumId, allMoves),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['photo-album', albumId] });
      qc.invalidateQueries({ queryKey: ['photo-albums'] });
      setError(null);
      onClose();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Bulk reassign failed'),
  });

  if (!open) return null;

  const sorted = [...entries].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg border border-slate-800 bg-slate-900 shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-white">Bulk reassign entries</h3>
            <p className="mt-0.5 text-xs text-slate-500">
              Use this after a chain of account transfers. Set each entry's new
              player. Leavers can be set to "— free entry —" (photo stays, no
              player attached).
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="pb-2 pr-2">Entry</th>
                <th className="pb-2 pr-2">Current player</th>
                <th className="pb-2">New player</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((e) => {
                const cur = e.player?.id ?? null;
                const wanted = moves.get(e.entryId) ?? null;
                const isDup = wanted != null && dupTargets.has(wanted);
                const changedRow = wanted !== cur;
                return (
                  <tr
                    key={e.entryId}
                    className={
                      'border-t border-slate-800/70 ' +
                      (changedRow ? 'bg-indigo-950/20' : '')
                    }
                  >
                    <td className="py-2 pr-2 text-slate-300">
                      <div className="flex items-center gap-2">
                        <div className="h-10 w-10 shrink-0 overflow-hidden rounded bg-slate-950">
                          {e.hasPhoto ? (
                            <img
                              src={api.photoAlbumEntryPhotoUrlById(
                                albumId,
                                e.entryId,
                                e.updatedAt ?? undefined,
                              )}
                              alt=""
                              className="h-full w-full object-cover"
                              style={{
                                objectPosition: `${e.focusX * 100}% ${e.focusY * 100}%`,
                              }}
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-[10px] text-slate-600">
                              no photo
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="truncate text-xs text-slate-400">
                            #{e.entryId}
                          </div>
                          {e.title && (
                            <div className="truncate text-xs text-slate-500">
                              {e.title}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-2 pr-2 text-slate-400">
                      {e.player
                        ? e.player.displayName ?? e.player.name
                        : <span className="italic text-slate-600">free</span>}
                    </td>
                    <td className="py-2">
                      <select
                        value={wanted ?? ''}
                        onChange={(ev) => {
                          const v = ev.target.value;
                          const next = new Map(moves);
                          next.set(e.entryId, v === '' ? null : Number(v));
                          setMoves(next);
                        }}
                        className={
                          'w-full rounded border bg-slate-950 px-2 py-1 text-xs text-slate-100 ' +
                          (isDup
                            ? 'border-red-600'
                            : 'border-slate-700')
                        }
                      >
                        <option value="">— free entry —</option>
                        {players.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.displayName ?? p.name}
                            {p.active ? '' : ' (inactive)'}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-slate-800 px-4 py-3">
          <div className="text-xs text-slate-500">
            {changedCount} change{changedCount === 1 ? '' : 's'}
            {dupTargets.size > 0 && (
              <span className="ml-2 text-red-400">
                {dupTargets.size} duplicate target{dupTargets.size === 1 ? '' : 's'}
              </span>
            )}
            {error && <span className="ml-2 text-red-400">· {error}</span>}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => applyMutation.mutate()}
              disabled={
                applyMutation.isPending || changedCount === 0 || dupTargets.size > 0
              }
              className="rounded bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-40"
            >
              {applyMutation.isPending ? 'Applying…' : `Apply ${changedCount}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ------------ empty player card + add-free ------------

function EmptyPlayerCard({
  albumId,
  player,
  readOnly,
}: {
  albumId: number;
  player: PhotoAlbumEntryPlayer;
  readOnly: boolean;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['photo-album', albumId] });
    qc.invalidateQueries({ queryKey: ['photo-albums'] });
  };

  const upload = useMutation({
    mutationFn: (file: File) => api.uploadPhotoAlbumEntryPhoto(albumId, player.id, file),
    onSuccess: () => {
      invalidate();
      setError(null);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Upload failed'),
  });

  const decline = useMutation({
    mutationFn: () => api.setPhotoAlbumEntryDeclined(albumId, player.id, true),
    onSuccess: () => {
      invalidate();
      setError(null);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Save failed'),
  });

  const name = player.displayName?.trim() || player.name;
  const subtitle = [player.rank, player.realName].filter(Boolean).join(' · ');

  return (
    <div className="flex items-center gap-2 rounded-lg border border-dashed border-slate-800 bg-slate-900/20 p-3">
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{name}</div>
        <div className="truncate text-xs text-slate-500">{subtitle}</div>
      </div>
      {!readOnly && (
        <>
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            disabled={upload.isPending}
            className="flex items-center gap-1 rounded bg-indigo-600 px-2 py-1 text-xs hover:bg-indigo-500 disabled:opacity-50"
          >
            <Upload size={12} /> {upload.isPending ? '…' : 'Upload'}
          </button>
          <button
            type="button"
            onClick={() => decline.mutate()}
            disabled={decline.isPending}
            title="Player won't send a photo — hide from this list"
            className="flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800 disabled:opacity-50"
          >
            <UserX size={12} /> Won't send
          </button>
          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) upload.mutate(f);
            }}
          />
        </>
      )}
      {error && <div className="text-xs text-red-400">{error}</div>}
    </div>
  );
}

function DeclinedSection({
  albumId,
  players,
  readOnly,
}: {
  albumId: number;
  players: PhotoAlbumEntryPlayer[];
  readOnly: boolean;
}) {
  const [open, setOpen] = useState(false);
  const sorted = [...players].sort((a, b) => {
    const r = (RANK_ORDER[b.rank] ?? 0) - (RANK_ORDER[a.rank] ?? 0);
    if (r) return r;
    return a.name.localeCompare(b.name);
  });
  return (
    <section className="space-y-2 pt-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-300"
      >
        <UserX size={14} />
        Won't send ({players.length})
        <span className="text-xs normal-case text-slate-600">
          {open ? 'hide' : 'show'}
        </span>
      </button>
      {open && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {sorted.map((p) => (
            <DeclinedPlayerCard
              key={p.id}
              albumId={albumId}
              player={p}
              readOnly={readOnly}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function DeclinedPlayerCard({
  albumId,
  player,
  readOnly,
}: {
  albumId: number;
  player: PhotoAlbumEntryPlayer;
  readOnly: boolean;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['photo-album', albumId] });
    qc.invalidateQueries({ queryKey: ['photo-albums'] });
  };
  const undo = useMutation({
    mutationFn: () => api.setPhotoAlbumEntryDeclined(albumId, player.id, false),
    onSuccess: () => {
      invalidate();
      setError(null);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Save failed'),
  });
  const name = player.displayName?.trim() || player.name;
  const subtitle = [player.rank, player.realName].filter(Boolean).join(' · ');
  return (
    <div className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-900/10 p-3 opacity-70">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm">{name}</div>
        <div className="truncate text-xs text-slate-500">{subtitle}</div>
      </div>
      {!readOnly && (
        <button
          type="button"
          onClick={() => undo.mutate()}
          disabled={undo.isPending}
          title="Move back to missing"
          className="flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800 disabled:opacity-50"
        >
          <Undo2 size={12} /> Undo
        </button>
      )}
      {error && <div className="text-xs text-red-400">{error}</div>}
    </div>
  );
}

function AddFreeEntryButton({
  albumId,
  readOnly,
}: {
  albumId: number;
  readOnly: boolean;
}) {
  const qc = useQueryClient();
  const create = useMutation({
    mutationFn: () => api.createFreePhotoAlbumEntry(albumId, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['photo-album', albumId] });
      qc.invalidateQueries({ queryKey: ['photo-albums'] });
    },
  });
  return (
    <button
      type="button"
      onClick={() => create.mutate()}
      disabled={readOnly || create.isPending}
      className="flex items-center gap-1 rounded border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800 disabled:opacity-50"
    >
      <ImagePlus size={14} /> {create.isPending ? 'Adding…' : 'Add free image'}
    </button>
  );
}

function Style() {
  return (
    <style>{`
      .input {
        width: 100%;
        background: rgb(2 6 23 / 1);
        border: 1px solid rgb(51 65 85 / 1);
        border-radius: 6px;
        padding: 6px 10px;
        font-size: 14px;
        color: inherit;
      }
      .input:focus { outline: none; border-color: rgb(99 102 241 / 1); }
    `}</style>
  );
}
